import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from '@/app/store';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getOrganisations } from '@/features/organisations/api/organisationsApi';
import { getUsers } from '@/features/users/api/usersApi';
import { queryKeys } from '@/shared/api/queryKeys';
import { getApiErrorMessage } from '@/shared/utils/apiError';

/**
 * App initialization hook - similar to the old useApp.js
 *
 * This hook manages the complete app bootstrap flow:
 * 1. Checks for existing auth (cookies)
 * 2. Performs auth flow if needed
 * 3. Fetches initial data (organisations and users) after successful auth
 * 4. Manages the "ready" state to block rendering until all initial data is loaded
 */
export function useAppInit() {
  const queryClient = useQueryClient();
  const { initializeAuth, isLoading: authLoading } = useAuth();
  const org_access_token = useAppSelector((state) => state.auth.org_access_token);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);
  const authInitializedRef = useRef(false);

  // Prefetch organisations and users data
  const prefetchInitialData = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    try {
      // Prefetch organisations and users in parallel
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: queryKeys.organisations.list({}),
          queryFn: () => getOrganisations({}),
        }),
        queryClient.prefetchQuery({
          queryKey: queryKeys.users.list({}),
          queryFn: () => getUsers({}),
        }),
      ]);
      setReady(true);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load initial data'));
      setReady(true); // Still mark as ready to allow app to render with error
    }
  }, [queryClient]);

  // Initialize auth on mount
  useEffect(() => {
    if (authInitializedRef.current) return;
    authInitializedRef.current = true;
    initializeAuth();
  }, [initializeAuth]);

  // Prefetch data after successful authentication
  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated || !org_access_token) {
      // If not loading and not authenticated, we're done (redirect will happen)
      setReady(true);
      return;
    }

    prefetchInitialData();
  }, [authLoading, isAuthenticated, org_access_token, prefetchInitialData]);

  return {
    ready,
    error,
    isLoading: authLoading || (!ready && isAuthenticated),
  };
}
